/**
 * Gate the people console on `access` **or** platform staff (ADR-0067).
 *
 * The one gate that is a disjunction, mirroring `@RequireAccessAuthority()` on the API: platform
 * staff sit above `access` rather than beside it — they alone grant it — so `RequireOpsFacet`
 * with `access` would send home the one person on a new box who can start administering people.
 *
 * A redirect, on `RequireOpsFacet`'s reasoning: the API refuses independently, so this is a
 * navigation courtesy and never the control itself. *
 * **It says why now (ADR-0085), for an Ops user and not for a Merchant one.** The redirect
 * above was the whole answer, so an operator following a colleague's link arrived at the
 * landing page with the URL gone from the address bar and nothing to go on. A Merchant user
 * still goes home, because that reasoning holds for them: this is not their screen and their
 * own is one route away. An Ops user who lacks one responsibility is told which. Its sentence names
 * both ways in, because naming one would send a reader to ask for the wrong grant — the same
 * reason `facetNeededFor` answers `null` for this kind.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from './session.js';
import { AuthorityRefused } from './authority-refused.js';

export function RequireAccessAuthority({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const { state } = useSession();

  if (state.status !== 'authenticated') return <>{children}</>;
  const { session } = state;
  if (session.role !== 'opsUser') return <Navigate to="/" replace />;
  if (!(session.platformStaff || session.opsFacets.includes('access'))) {
    return <AuthorityRefused needs={{ kind: 'accessAuthority' }} />;
  }

  return <>{children}</>;
}
